import {
  ADD_SPACES_LEVEL_STEP_PREFIX,
  getAddSpacesLevelStepId
} from '@alpinehomeair/common';

const SUB_STEP_TO_STEP_CODE: Record<string, string> = {
  configure_level_details: 'floors_and_levels',
  images: 'floors_and_levels',
  confirm_details: 'structure_details',
  condenser_locations: 'condenser_locations',
  orientation: 'base_layout',
  add_spaces: 'base_layout',
  space_details: 'space_details',
  sizing_tuning: 'sizing_tuning'
};

export { getAddSpacesLevelStepId };

export function getStepCodeForSubStep(subStepId: string): string | null {
  if (subStepId.startsWith(ADD_SPACES_LEVEL_STEP_PREFIX)) {
    return 'base_layout';
  }
  return SUB_STEP_TO_STEP_CODE[subStepId] ?? null;
}

export function isBaseLayoutStep(stepId: string): boolean {
  return (
    stepId.startsWith(ADD_SPACES_LEVEL_STEP_PREFIX) ||
    stepId === 'add_spaces' ||
    stepId === 'base_layout'
  );
}

export type StructureStepRef = {
  stepCode: string;
  subStepCode: string | null;
};

export type StructureStepPayload = {
  structureUniqueId: string;
  stepCode: string;
  subStepCode: string | null;
  completed: boolean;
  configurationUniqueId?: string | null;
};

export function buildResetPayloads(
  structureUniqueId: string,
  stepsToReset: string[]
): StructureStepPayload[] {
  const payloads: StructureStepPayload[] = [];
  for (const stepId of stepsToReset) {
    const stepCode = getStepCodeForSubStep(stepId);
    if (stepCode === null) {
      payloads.push({
        structureUniqueId,
        stepCode: stepId,
        subStepCode: null,
        completed: false
      });
    } else {
      payloads.push({
        structureUniqueId,
        stepCode,
        subStepCode: stepId,
        completed: false
      });
    }
  }
  return payloads;
}

export type StructureStepDeletePayload = {
  structureUniqueId: string;
  stepCode: string;
  subStepCode: string | null;
  configurationUniqueId?: string | null;
};

export function buildDeletePayloads(
  structureUniqueId: string,
  stepsToDelete: string[]
): StructureStepDeletePayload[] {
  const payloads: StructureStepDeletePayload[] = [];
  for (const stepId of stepsToDelete) {
    const stepCode = getStepCodeForSubStep(stepId);
    if (stepCode === null) {
      payloads.push({
        structureUniqueId,
        stepCode: stepId,
        subStepCode: null
      });
    } else {
      payloads.push({
        structureUniqueId,
        stepCode,
        subStepCode: stepId
      });
    }
  }
  return payloads;
}

export const STRUCTURE_PAGE_STEP_CODES = [
  'floors_and_levels',
  'structure_details',
  'base_layout',
  'space_details',
  'extra_details',
  'sizing_tuning'
];

export const NO_SPACES_AFFECTED_STEP_IDS = [
  'condenser_locations',
  'orientation',
  'space_details',
  'add_spaces',
  'base_layout'
];

export const CONDITIONED_LEVEL_AFFECTED_STEP_IDS = [
  'condenser_locations',
  'orientation',
  'space_details',
  // 'extra_details' stays: legacy completed-step rows for the removed
  // electrical_panel/existing_equipment sub-steps still reset under it.
  'extra_details',
  'sizing_tuning'
];

export function getNoSpacesAffectedSteps(
  manuallyCompletedSteps: string[]
): string[] {
  const affected: string[] = [];
  for (const stepId of manuallyCompletedSteps) {
    if (
      stepId.startsWith(ADD_SPACES_LEVEL_STEP_PREFIX) ||
      NO_SPACES_AFFECTED_STEP_IDS.includes(stepId)
    ) {
      affected.push(stepId);
    }
  }
  return affected;
}
